import React, {
  createRef,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useMediaQuery } from "react-responsive";
import { useMutationObserver } from "@react-hooks-library/core";
import useInterval from "use-interval";
import { useInView } from "react-intersection-observer";
import classNames from "classnames";
import NextSlideButton from "../SectionNavigation/NextSlideButton";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import { useOverlayContext } from "../../functional-components/OverlayContext";
import "./style.scss";
import { scrollIntoViewHorizontally } from "../../utils/ScrollUtils";
import useWindowSize from "../../hooks/useWindowSize";

export type CarouselProps = {
  slidesData: Record<string, unknown>[];
  renderSlide: (data: Record<string, unknown>, index: number) => ReactNode;
  renderThumb?: (data: Record<string, unknown>, index: number) => ReactNode;
  renderTab?: (data: Record<string, unknown>, index: number) => ReactNode;
  renderThumbPlaceholder?: () => ReactNode;
  renderTabPlaceholder?: () => ReactNode;
  navType?: "thumbs" | "dots" | "tabs" | "arrows";
  direction?: "horizontal" | "vertical";
  loop?: boolean; //seamless looping
  startOverAtEnd?: boolean; //looping by scrolling back to first slide
  showMarkers?: boolean;
  smallMarkers?: boolean;
  hideButtons?: boolean;
  centeredButtons?: boolean;
  fixedWidthSlides?: boolean;
  autoplayInterval?: number;
  startIndex?: number;
  onSlideChange?: (slideIndex: number) => void;
  offset?: number;
  className?: string;
};

export type CarouselHandle = {
  gotoNext: (reverse?: boolean) => void;
  gotoSlide: (slideIndex: number) => void;
  gotoIndex: (slideIndex: number) => void;
  gotoNextPage: (reverse?: boolean) => void;
};

const Carousel = forwardRef<CarouselHandle, CarouselProps>(function Carousel(
  props: CarouselProps,
  ref
) {
  ref = ref || createRef();

  const {
    slidesData = [],
    renderSlide,
    renderThumb,
    renderTab,
    renderThumbPlaceholder,
    renderTabPlaceholder,
    navType = "thumbs",
    direction = "horizontal",
    loop = false,
    startOverAtEnd = false,
    showMarkers = false,
    smallMarkers = false,
    hideButtons = false,
    centeredButtons = false,
    fixedWidthSlides = false,
    autoplayInterval = 0,
    startIndex = 0,
    onSlideChange,
    offset = 0,
    className
  } = props;

  const { overlayOpened, flyoutOpened } = useOverlayContext();

  const scrollAttr = direction === "vertical" ? "top" : "left";
  const offsetAttr = direction === "vertical" ? "offsetTop" : "offsetLeft";
  //const scrollValueAttr = direction === "vertical" ? "scrollTop" : "scrollLeft";
  const sizeAttr = direction === "vertical" ? "clientHeight" : "clientWidth";

  const isTouchDevice = useMediaQuery({ query: "(pointer: coarse)" });
  const winDim = useWindowSize();
  const isSmall = useMediaQuery({ maxWidth: 599.99 });
  const container = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [scrollDuration, setScrollDuration] = useState<number>(1000);
  const [slidesFitsInViewport, setSlidesFitsInViewport] =
    useState<boolean>(true);
  const track = useRef<HTMLDivElement>(null);
  const [slideElms, setSlideElms] = useState<HTMLElement[]>([]);
  const tabContainer = useRef<HTMLDivElement>(null);
  const tabContainerInner = useRef<HTMLDivElement>(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const _activeIndex = useRef(activeIndex);
  const initialStartIndex = useRef(startIndex);
  const [lastVisibleIndex, setLastVisibleIndex] = useState(0);
  const [scrollToPos, setScrollToPos] = useState(0);
  const [scrollToTab, setScrollToTab] = useState(0);
  const indexQueueOffset = useRef(0);
  const [paused, setPaused] = useState(false);
  const [allSlidesLoaded, setAllSlidesLoaded] = useState<boolean>(false);
  const [inViewRef, inView] = useInView();

  useLayoutEffect(() => {
    const ua = navigator.userAgent;
    const isSafari = ua.indexOf("Safari") > -1 && ua.indexOf("Chrom") === -1;
    setContainerWidth(container.current?.[sizeAttr] || 0);
    setSlidesFitsInViewport(
      (track.current?.offsetWidth || 0) <= (container.current?.[sizeAttr] || 0)
    );
    //An approximation of the duration of a smooth scrollTo
    setScrollDuration(isSafari ? 500 : 900);
  }, [sizeAttr, winDim.innerWidth]);

  useEffect(() => {
    container.current?.scrollTo({
      [scrollAttr]: scrollToPos,
      behavior: "smooth"
    });
  }, [scrollAttr, scrollToPos]);

  useEffect(() => {
    const tab = tabContainerInner.current?.children[scrollToTab];
    if (tab) scrollIntoViewHorizontally(tabContainer.current, tab);
  }, [scrollAttr, scrollToTab]);

  const scrollTabIntoView = useCallback(
    (index: number) => {
      if (navType === "tabs" && isSmall) {
        setScrollToTab(index);
      }
    },
    [isSmall, navType]
  );

  const actualSlideCount = slideElms.length - (loop ? 2 : 0);

  const presentSlides: boolean[] = slidesData.map(
    (data, i) =>
      slideElms.findIndex(
        elm => elm.getAttribute("data-slideid") === "slide-" + i
      ) > -1
  );

  const onDOMUpdate = useCallback(() => {
    //In some cases, when the carousel is inside an overlay, this delay is needed for loaded to be true.
    setTimeout(() => {
      const _slideElms = track.current
        ? Array.from(track.current.childNodes.values()).map(
            n => n as HTMLElement
          )
        : [];

      const loaded =
        _slideElms.findIndex(slide => slide.childElementCount === 0) === -1 &&
        _slideElms.findIndex(slide => slide?.[sizeAttr] === 0) === -1 &&
        container.current?.getElementsByClassName("loading").length === 0;

      if (loaded) {
        console.log("Carousel: All slides loaded");
        setAllSlidesLoaded(true);
      }

      setSlideElms(_slideElms);
    }, 200);
  }, [sizeAttr]);

  useLayoutEffect(onDOMUpdate, [onDOMUpdate]);
  useMutationObserver(track, onDOMUpdate, { subtree: true, childList: true });

  const getNearestSlidePos = useCallback(
    (targetPos: number) => {
      if (allSlidesLoaded) {
        const actualSlideCount = slideElms.length - (loop ? 2 : 0);
        const slidePositions: number[] = slideElms.map(
          slide => slide[offsetAttr]
        );
        let nearestIndex = slidePositions.findIndex((pos, index) => {
          const halfSlideWidth =
            (index < slideElms.length - 1
              ? slidePositions[index + 1] - slidePositions[index]
              : slidePositions[1] - slidePositions[0]) / 2;
          return pos + halfSlideWidth > targetPos;
        });
        const slidePositionsRev = slidePositions.slice().reverse();
        const lastVisibleIndexRev = slidePositionsRev.findIndex(
          (pos, index) => {
            const lastSlideWidthMinusThreshold =
              (index > 1
                ? slidePositionsRev[index - 1] - slidePositionsRev[index]
                : slidePositionsRev[0] - slidePositionsRev[1]) * 0.97;
            return (
              pos + lastSlideWidthMinusThreshold < targetPos + containerWidth
            );
          }
        );
        const lastVisibleIndex = slideElms.length - 1 - lastVisibleIndexRev;
        const actualNearestPos = slideElms[nearestIndex]
          ? slideElms[nearestIndex]?.[offsetAttr] - offset
          : undefined;
        let needsPosChange = false;
        if (nearestIndex === -1) {
          nearestIndex = slidePositions.length - 1;
        }
        if (loop && nearestIndex >= actualSlideCount + 1) {
          nearestIndex = 1;
          needsPosChange = true;
        }
        if (loop && nearestIndex === 0) {
          nearestIndex = actualSlideCount;
          needsPosChange = true;
        }
        const nearestPos = slideElms[nearestIndex]?.[offsetAttr] - offset;
        const realIndex =
          (nearestIndex - (loop ? 1 : 0) + actualSlideCount) % actualSlideCount;
        const realLastVisibleIndex =
          (lastVisibleIndex - (loop ? 1 : 0) + actualSlideCount) %
          actualSlideCount;

        /*console.log(
          "Carousel::getNearestSlidePos",
          targetPos,
          nearestPos,
          nearestIndex,
          realIndex,
          realLastVisibleIndex,
          needsPosChange,
          actualNearestPos
        );*/

        return {
          nearestPos,
          realIndex,
          realLastVisibleIndex,
          needsPosChange,
          actualNearestPos
        };
      } else {
        return {
          nearestPos: undefined,
          realIndex: undefined,
          realLastVisibleIndex: undefined,
          needsPosChange: undefined,
          actualNearestPos: undefined
        };
      }
    },
    [allSlidesLoaded, slideElms, loop, offsetAttr, offset, containerWidth]
  );

  useLayoutEffect(() => {
    const _container = container.current;
    const scrollHandler = () => {
      const scrollPos =
        direction === "vertical"
          ? container.current?.scrollTop
          : container.current?.scrollLeft;
      if (allSlidesLoaded && scrollPos !== undefined) {
        const {
          nearestPos,
          realIndex,
          realLastVisibleIndex,
          needsPosChange,
          actualNearestPos
        } = getNearestSlidePos(scrollPos);

        if (needsPosChange && actualNearestPos === scrollPos) {
          //console.log("Carousel: Needs pos change:", nearestPos);
          container.current?.scrollTo({
            [scrollAttr]: nearestPos,
            behavior: "instant"
          });
        }
        if (
          realLastVisibleIndex !== undefined &&
          lastVisibleIndex !== realLastVisibleIndex
        ) {
          setLastVisibleIndex(realLastVisibleIndex);
        }

        if (realIndex !== undefined && _activeIndex.current !== realIndex) {
          setActiveIndex(realIndex);
          _activeIndex.current = realIndex; //to avoid having activeIndex as dependency
          scrollTabIntoView(realIndex);
          onSlideChange?.(realIndex);
          indexQueueOffset.current = 0;
        }
      }
    };
    _container?.addEventListener("scroll", scrollHandler);
    //Trigger on initialization:
    scrollHandler();
    return () => _container?.removeEventListener("scroll", scrollHandler);
  }, [
    allSlidesLoaded,
    direction,
    getNearestSlidePos,
    lastVisibleIndex,
    onSlideChange,
    scrollAttr,
    scrollTabIntoView
  ]);

  const gotoSlide = useCallback(
    (index: number) => {
      //console.log("Carousel::gotoSlide", index);
      const actualSlideCount = slideElms.length - (loop ? 2 : 0);
      if (
        index >= (loop ? -1 : 0) &&
        index < actualSlideCount + (loop ? 1 : 0)
      ) {
        const slidePositions: number[] = slideElms
          .map(slide => slide[offsetAttr])
          .filter((pos, index) => pos > 0 || index === 0);

        const realIndex = (index + actualSlideCount) % actualSlideCount;
        const lookupIndex = loop ? index + 1 : index;
        const halfWidth = containerWidth * 0.5;
        const slideWidth = slideElms[lookupIndex]?.[sizeAttr] || 0;

        setScrollToPos(
          slidePositions[lookupIndex] - offset - halfWidth + slideWidth / 2
        );
        setTimeout(() => {
          scrollTabIntoView(realIndex);
        }, scrollDuration);
      }
    },
    [
      containerWidth,
      loop,
      offset,
      offsetAttr,
      scrollDuration,
      scrollTabIntoView,
      sizeAttr,
      slideElms
    ]
  );

  const gotoNext = useCallback(
    (reverse: boolean = false) => {
      const actualSlideCount = slideElms.length - (loop ? 2 : 0);

      indexQueueOffset.current += reverse ? -1 : 1;
      const nextIndex = reverse
        ? Math.max(activeIndex + indexQueueOffset.current, loop ? -1 : 0)
        : Math.min(
            activeIndex + indexQueueOffset.current,
            actualSlideCount - (loop ? 0 : 1)
          );
      gotoSlide(nextIndex);
    },
    [activeIndex, gotoSlide, loop, slideElms.length]
  );

  /** This behaves different from gotoNext if the slides do not have the same width as the container */
  const gotoNextPage = useCallback(
    (reverse: boolean = false) => {
      indexQueueOffset.current += reverse ? -1 : 1;
      const startOver =
        !reverse && startOverAtEnd && lastVisibleIndex >= actualSlideCount - 1;

      const scrollPos =
        direction === "vertical"
          ? container.current?.scrollTop || 0
          : container.current?.scrollLeft || 0;
      const targetPos = startOver
        ? 0
        : scrollPos + indexQueueOffset.current * containerWidth * 0.95;
      const { nearestPos } = getNearestSlidePos(targetPos);

      if (nearestPos !== undefined) {
        setScrollToPos(nearestPos);
      }
    },
    [
      startOverAtEnd,
      lastVisibleIndex,
      actualSlideCount,
      direction,
      containerWidth,
      getNearestSlidePos
    ]
  );

  useInterval(
    () => {
      if (!paused && inView && !overlayOpened && !flyoutOpened) {
        gotoNext();
      }
    },
    autoplayInterval > 0 ? autoplayInterval * 1000 : false
  );

  //To call methods from outside:
  useImperativeHandle(ref, () => ({
    gotoNext,
    gotoSlide,
    gotoIndex: gotoSlide,
    gotoNextPage
  }));

  const slides = useMemo(
    () =>
      React.Children.map(slidesData.map(renderSlide), (element, i) => {
        return React.cloneElement(element as ReactElement, {
          "data-slideid": "slide-" + i
        });
      }),
    [renderSlide, slidesData]
  );

  const thumbs = useMemo(
    () =>
      renderThumb && allSlidesLoaded
        ? slidesData
            .filter((slide, i) => presentSlides[i])
            .map((slide, index) =>
              renderThumb(
                {
                  ...slide,
                  isActive: index >= activeIndex && index <= lastVisibleIndex,
                  onClick: () => {
                    setPaused(true);
                    gotoSlide(index);
                  }
                },
                index
              )
            )
        : renderThumb && renderThumbPlaceholder
        ? renderThumbPlaceholder()
        : [],
    [
      activeIndex,
      allSlidesLoaded,
      gotoSlide,
      lastVisibleIndex,
      presentSlides,
      renderThumb,
      renderThumbPlaceholder,
      slidesData
    ]
  );

  const tabs = useMemo(
    () =>
      renderTab && allSlidesLoaded
        ? slidesData
            .filter((slide, i) => presentSlides[i])
            .map((slide, index) =>
              renderTab(
                {
                  ...slide,
                  isActive: index >= activeIndex && index <= lastVisibleIndex,
                  onClick: () => {
                    setPaused(true);
                    gotoSlide(index);
                  }
                },
                index
              )
            )
        : renderTab && renderTabPlaceholder
        ? renderTabPlaceholder()
        : [],
    [
      activeIndex,
      allSlidesLoaded,
      gotoSlide,
      lastVisibleIndex,
      presentSlides,
      renderTab,
      renderTabPlaceholder,
      slidesData
    ]
  );

  const markers = useMemo(
    () =>
      slidesData.map((s, i) => (
        <div
          role="presentation"
          key={i}
          className={classNames("marker", {
            active: i >= activeIndex && i <= lastVisibleIndex
          })}
          onClick={() => {
            setPaused(true);
            gotoSlide(i);
          }}
        ></div>
      )),
    [activeIndex, gotoSlide, lastVisibleIndex, slidesData]
  );

  useLayoutEffect(() => {
    /** When loop is true, the first slide is a copy of the last one, so move to the next */
    if (slideElms && slideElms.length > 1) {
      const firstSlideWidth: number = slideElms[0]?.[sizeAttr] || 0;
      if (initialStartIndex.current === 0 && loop) {
        //console.log("Carousel: Jump to first slide", firstSlideWidth, offset);
        container.current?.scrollTo({
          [scrollAttr]: firstSlideWidth - offset,
          behavior: "instant"
        });
        setScrollToPos(firstSlideWidth - offset);
      }
      if (initialStartIndex.current !== 0) {
        const slidePositions: number[] = slideElms
          .map(slide => slide[offsetAttr])
          .filter((pos, index) => pos > 0 || index === 0);
        const pos =
          slidePositions[initialStartIndex.current + (loop ? 1 : 0)] - offset;
        /*console.log(
          "Carousel: scroll to startIndex",
          initialStartIndex.current,
          pos,
          slidePositions
        );*/
        container.current?.scrollTo({
          [scrollAttr]: pos,
          behavior: "instant"
        });
        setScrollToPos(pos);
      }
    }
  }, [loop, offset, offsetAttr, scrollAttr, sizeAttr, slideElms]);

  return (
    <>
      <div
        className={classNames(
          "carousel",
          "carousel-navtype-" + navType,
          className
        )}
        ref={inViewRef}
      >
        {navType === "tabs" &&
          tabs &&
          !(fixedWidthSlides && slidesFitsInViewport) && (
            <div
              ref={tabContainer}
              className={classNames("carousel-tabs")}
              aria-hidden
            >
              <div
                className={classNames("carousel-tabs-inner")}
                ref={tabContainerInner}
              >
                {tabs}
              </div>
            </div>
          )}
        <div ref={container} className="carousel-container">
          <div
            ref={track}
            className={classNames(
              "carousel-slides",
              "carousel-slides-" + direction,
              { "carousel-fixed-width-slides": fixedWidthSlides }
            )}
            onTouchMove={() => setPaused(true)}
            onWheel={e => {
              if (
                (direction === "horizontal" &&
                  e.deltaY == 0 &&
                  e.deltaX !== 0) ||
                (direction === "vertical" && e.deltaX == 0 && e.deltaY !== 0)
              ) {
                setPaused(true);
              }
            }}
          >
            {loop &&
              slides &&
              slides.length > 1 &&
              React.cloneElement(
                allSlidesLoaded ? slides[slides.length - 1] : slides[0],
                {
                  key: "loop-slide-back",
                  "aria-hidden": "true",
                  disableInteraction: true
                }
              )}
            {slides}
            {loop &&
              slides &&
              slides.length > 1 &&
              allSlidesLoaded &&
              React.cloneElement(slides[0], {
                key: "loop-slide-forward",
                "aria-hidden": "true",
                disableInteraction: true
              })}
          </div>
        </div>
        {!isTouchDevice &&
          direction === "horizontal" &&
          !isSmall &&
          (!hideButtons || navType === "arrows") &&
          allSlidesLoaded &&
          slides &&
          slides.length > 1 &&
          !(fixedWidthSlides && slidesFitsInViewport) && (
            /* back */ <NextSlideButton
              onClick={() => {
                gotoNextPage(true);
              }}
              slideDirection={direction}
              direction={-1}
              returnIcon={activeIndex === 0}
              centered={centeredButtons}
              disabled={!loop && activeIndex <= 0}
              ariaHidden
            />
          )}
        {!isTouchDevice &&
          (!hideButtons || navType === "arrows") &&
          allSlidesLoaded &&
          slides &&
          slides.length > 1 &&
          !(fixedWidthSlides && slidesFitsInViewport) /* forward */ && (
            <NextSlideButton
              onClick={() => {
                gotoNextPage(false);
              }}
              slideDirection={direction}
              direction={1}
              centered={centeredButtons}
              returnIcon={
                startOverAtEnd && lastVisibleIndex >= actualSlideCount - 1
              }
              disabled={
                !loop &&
                !startOverAtEnd &&
                lastVisibleIndex >= actualSlideCount - 1
              } /* TODO: does not work for non-full-width slides */
              ariaHidden
            />
          )}

        {navType === "thumbs" &&
        !isSmall &&
        thumbs &&
        !(fixedWidthSlides && slidesFitsInViewport) ? (
          <div className={classNames("carousel-thumbs")} aria-hidden>
            {thumbs}
          </div>
        ) : (
          (navType === "dots" ||
            ((navType === "arrows" ||
              navType === "thumbs" ||
              navType === "tabs") &&
              showMarkers)) &&
          direction === "horizontal" &&
          allSlidesLoaded &&
          slides &&
          slides.length > 1 && (
            <div
              className={classNames("carousel-horizontal-markers", {
                "small-markers": smallMarkers,
                "bold-markers": navType === "tabs",
                dots: navType === "dots"
              })}
            >
              <div className="markers-container">{markers}</div>
            </div>
          )
        )}
      </div>
      <KeyboardEventHandler
        handleKeys={["left", "right"]}
        handleFocusableElements
        isDisabled={!!slides && slides.length <= 1}
        onKeyEvent={key => {
          switch (key) {
            case "left":
              gotoNext(true);
              break;
            case "right":
              gotoNext(false);
              break;
            default:
          }
        }}
      />
    </>
  );
});

export default Carousel;
